import { useComputed } from '@preact/signals-react';
import { duration, getDayOfWeek } from '~/lib/datetime/mod.js';
import { compose } from '~/lib/fn/mod.js';
import { capitalize, pluralize, tkns } from '~/lib/str/mod.js';
import { formatCurrency } from '~/lib/num/mod.js';

import { withConfig } from '~/features/Config/mod.js';
import { BookmarkButton } from '~/features/Bookmarks/mod.ui.js';

import { Heading, Text } from '~/ui/Typography/mod.js';
import { withAppState } from '~/features/AppState/mod.js';

import { scheduleSearchIndex } from '~/apps.deps.js';

import { useGlobalSearch } from './search.js';

const mapConfigToProps = {
	displayRoomStyle: 'locationDisplay',
};

const compClassGen = (subTarget = '', includeDot = false) =>
	`${includeDot ? '.' : ''}schedule-item${subTarget ? `-${subTarget}` : ''}`;

export const compClass = Object.freeze({
	bare: subTarget => compClassGen(subTarget),
	dot: subTarget => compClassGen(subTarget, true),
});

/**
 * A Schedule Entry component
 *
 * @param {Object} props Component props
 * @param {import("./EventSession.js").EventSession} props.eventSession The schedule entry data
 * @param {1|2|3|4|5|6} [props.headingLevel=3] The top heading level for the entry
 * @param {import('~/')} [props.displayRoomStyle] A CSS class to apply to the root element
 * @param {string} [props.className] A CSS class to apply to the root element
 * @param {boolean} [props.isLongRunning=false] Marks the entry for the Now jump to stop targeting it
 *   an hour after it starts
 * @returns A rendered component
 */
const EventSessionEntry = ({
	eventSession,
	headingLevel = 3,
	displayRoomStyle = 'venue',
	className,
	isLongRunning = false,
	eventData,
}) => {
	const isBookmarked = useComputed(() => eventData.bookmarks.value[eventSession._id] === true);
	const [matches] = useGlobalSearch(searchText => {
		if (!searchText.trim()) {
			return false;
		}
		return scheduleSearchIndex.matches(eventSession._id, searchText.trim());
	});

	return (
		<article
			id={`session-${eventSession._id}`}
			className={tkns(className, {
				'is-bookmarked': isBookmarked.value,
				'is-matched': matches.value,
			})}
			data-long-running={isLongRunning || undefined}
		>
			<header className={compClass.bare('header')}>
				<Heading headingLevel={headingLevel} className={compClass.bare('heading')}>
					{eventSession.title}
				</Heading>
				<BookmarkButton
					itemTitle={eventSession.title}
					itemId={eventSession._id}
					isPressed={isBookmarked.value}
					className={compClass.bare('bookmark-action')}
				/>
			</header>
			<Text as="dl" className={compClass.bare('details')}>
				<div className={compClass.bare('presenters')}>
					<dt>{pluralize(eventSession.presenters.length, 'Presenter((s))')}</dt>
					<dd>{eventSession.presenters.join(', ')}</dd>
				</div>
				<div className={compClass.bare('when')}>
					<dt>When</dt>
					<dd>
						<time
							className={tkns('js-startTime', compClass.bare('start-time'))}
							dateTime={eventSession.getLocalStartDateTime().toISOString()}
						>
							{capitalize(getDayOfWeek(eventSession.startDate, true))},{' '}
							{eventSession.startTime.to12Hours()}
						</time>
						&ndash;
						<time
							className={tkns('js-endTime', compClass.bare('end-time'))}
							dateTime={eventSession.getLocalEndDateTime().toISOString()}
						>
							{eventSession.endTime.to12Hours()}
						</time>{' '}
						({duration(eventSession.getDurationInMilliseconds())})
					</dd>
				</div>
				<div className={compClass.bare('location')}>
					<dt>Location</dt>
					<dd>{eventSession.location.getLocationInfo(displayRoomStyle)}</dd>
				</div>
				{eventSession.cost ?
					<div className={compClass.bare('cost')}>
						<dt>Materials Cost</dt>
						<dd>
							{formatCurrency(eventSession.cost)}{' '}
							{eventSession.registrationUrl ?
								<a href={eventSession.registrationUrl} target="_blank" rel="nofollow noreferrer">
									Register
								</a>
							:	null}
						</dd>
					</div>
				:	null}
				{eventSession.maxAttendance ?
					<div className={compClass.bare('max-attendance')}>
						<dt>Max Attendance</dt>
						<dd>{eventSession.maxAttendance}</dd>
					</div>
				:	null}
			</Text>

			<Text className={compClass.bare('description')}>{eventSession.description}</Text>
		</article>
	);
};

export { EventSessionEntry };
export default compose(withConfig(mapConfigToProps), withAppState)(EventSessionEntry);
